import { useIsGlobalTeamAdmin } from '@common/teams/hooks';
import { DownloadBIcon, FileCheckBIcon, LinkExternalAIcon } from '@prophecy/icons';
import { BaseColor, theme } from '@prophecy/theme';
import { Badges, Button, Stack, StackItem, Text } from '@prophecy/ui-v3';
import { formattedDateTime } from '@prophecy/utils/date';

import { useGlobalVariables } from '../../../context/globalConfig/securedConfigHook';
import { TermsResponseStatus } from '../../../data/apis/terms';
import { SapAiTermsTestIds } from '../constants';
import { useSapAiTermsResponse } from '../hooks/useSapAiTermsResponse';
import { downloadSapAiTerms } from '../Pdf/downloadSapAiTerms';
import { TermsRunsText } from '../SapAiTermsBody';
import { useSapAiTermsDialog } from '../sapAiTermsDialogContext';
import { AI_SERVICES_LIST_LABEL, AI_UNIT_SUPPLEMENT_LABEL, SAP_TRUST_CENTER_URL, sapAiTerms } from '../sapTermsContent';
import { NoWrapText } from '../styled';
import { LegalCard, LegalCardDivider } from './styled';

const STATUS_LABEL: Record<TermsResponseStatus, string> = {
  [TermsResponseStatus.accepted]: 'Accepted',
  [TermsResponseStatus.denied]: 'Denied',
  [TermsResponseStatus.deferred]: 'Pending decision',
  [TermsResponseStatus.notResponded]: 'Not yet responded'
};

const STATUS_TONE: Record<TermsResponseStatus, BaseColor> = {
  [TermsResponseStatus.accepted]: BaseColor.success,
  [TermsResponseStatus.denied]: BaseColor.error,
  [TermsResponseStatus.deferred]: BaseColor.warning,
  [TermsResponseStatus.notResponded]: BaseColor.gray
};

export function LegalAiTermsCard() {
  const { isSapAiScrollWrapEnabled } = useGlobalVariables();
  const { isGlobalTeamAdmin: isAdmin } = useIsGlobalTeamAdmin({ enabled: isSapAiScrollWrapEnabled });
  const { openDialog } = useSapAiTermsDialog();
  const { status, respondedBy, respondedAt, isLoading, isError } = useSapAiTermsResponse({
    enabled: isSapAiScrollWrapEnabled
  });

  // LegalAiTermsCard is visible only for the old sap users, new sap users will have ai by default
  if (!isSapAiScrollWrapEnabled) return null;

  const respondedName = respondedBy?.name || respondedBy?.email;
  const showReviewButton = isAdmin && status !== TermsResponseStatus.accepted;

  return (
    <LegalCard direction='vertical' gap={theme.spaces.x16} data-test-id={SapAiTermsTestIds.legalCard}>
      <Text level='sm' weight={theme.fontWeight.semiBold} tone={theme.textColor.primary}>
        Legal & AI terms
      </Text>

      <Stack direction='vertical' gap={theme.spaces.x12}>
        <Stack direction='horizontal' align='space-between' alignY='start' gap={theme.spaces.x12}>
          <Stack direction='horizontal' gap={theme.spaces.x8} alignY='start'>
            <FileCheckBIcon type='default' />
            <Stack direction='vertical' gap={theme.spaces.x2}>
              <Stack direction='horizontal' gap={theme.spaces.x8} alignY='center'>
                <NoWrapText>
                  <Text level='sm' tone={theme.textColor.secondary}>
                    {`${sapAiTerms.title} · ${sapAiTerms.version}`}
                  </Text>
                </NoWrapText>
                {!isLoading ? (
                  <Badges
                    variant='pillLight'
                    tone={isError ? BaseColor.gray : STATUS_TONE[status]}
                    size='s'
                    data-test-id={SapAiTermsTestIds.legalCardStatusBadge}>
                    {isError ? 'Status unavailable' : STATUS_LABEL[status]}
                  </Badges>
                ) : null}
              </Stack>
              {!isError && respondedName && respondedAt && (
                <Text level='xs' tone={theme.colors.gray500} data-test-id={SapAiTermsTestIds.legalCardResponderMeta}>
                  {`${STATUS_LABEL[status]} ${formattedDateTime(respondedAt, { month: 'short', day: 'numeric', year: 'numeric' })} by ${respondedName}`}
                </Text>
              )}
            </Stack>
          </Stack>

          <Stack direction='horizontal' gap={theme.spaces.x8} alignY='center'>
            <Button
              variant='link'
              icon={<DownloadBIcon type='default' />}
              iconPlacement='left'
              onClick={downloadSapAiTerms}
              aria-label={showReviewButton ? 'Download terms' : undefined}
              data-test-id={SapAiTermsTestIds.legalCardDownloadButton}>
              {!showReviewButton && 'Download'}
            </Button>
            {showReviewButton ? (
              <Button
                variant='secondary'
                size='s'
                onClick={openDialog}
                data-test-id={SapAiTermsTestIds.legalCardReviewButton}>
                Review Terms
              </Button>
            ) : null}
          </Stack>
        </Stack>

        <Stack direction='horizontal' gap={theme.spaces.x8} alignY='center'>
          <LinkExternalAIcon type='default' />
          <NoWrapText>
            <Text level='sm' tone={theme.textColor.secondary}>
              {`${AI_UNIT_SUPPLEMENT_LABEL} · ${AI_SERVICES_LIST_LABEL}`}
            </Text>
          </NoWrapText>
          <StackItem grow='1' />
          <Button
            size='s'
            variant='link'
            elementType='anchor'
            href={SAP_TRUST_CENTER_URL}
            target='_blank'
            rel='noopener noreferrer'
            data-test-id={SapAiTermsTestIds.legalCardOpenLinkButton}>
            Open
          </Button>
        </Stack>
      </Stack>

      <LegalCardDivider />

      <TermsRunsText paragraph={sapAiTerms.closingParagraph} level='xs' />
    </LegalCard>
  );
}
